/**
 * Voice Navigation Engine
 * Enterprise-grade turn-by-turn navigation with voice announcements
 * 
 * Integrates with Google Directions API and provides:
 * - Real-time position tracking
 * - Voice-guided turn-by-turn directions
 * - Off-route detection and rerouting
 * - Last-mile compass navigation for lease roads
 * - Arrival detection and announcements
 * 
 * @module VoiceNavigationEngine
 * @version 1.0.0
 */

import { textToSpeech } from './textToSpeech';
import { navigationService, NavigationRoute, NavigationStep, NavigationState } from '../navigation/navigationService';
import { toast } from 'sonner';

// Re-export NavigationStep for consumers
export type { NavigationStep };

// Navigation status type
export type NavigationStatus = 'idle' | 'navigating' | 'rerouting' | 'arrived' | 'error';

// ============================================================================
// TYPES
// ============================================================================

export interface NavigationDestination {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
}

export interface NavigationProgress {
  isActive: boolean;
  destination: NavigationDestination | null;
  currentStepIndex: number;
  totalSteps: number;
  distanceRemaining: number; // meters
  etaMinutes: number;
  currentInstruction: string;
  nextInstruction?: string;
  isOffRoute: boolean;
  isArrived: boolean;
}

export type NavigationEventType =
  | 'started'
  | 'step_changed'
  | 'approaching_turn'
  | 'off_route'
  | 'rerouting'
  | 'arrived'
  | 'stopped'
  | 'error';

export interface NavigationEvent {
  type: NavigationEventType;
  data?: any;
  timestamp: number;
}

export type NavigationEventListener = (event: NavigationEvent) => void;

// ============================================================================
// CONSTANTS
// ============================================================================

const METERS_TO_FEET = 3.28084;
const METERS_TO_MILES = 0.000621371;
const ARRIVAL_THRESHOLD_METERS = 50; // ~150 feet
const OFF_ROUTE_THRESHOLD_METERS = 100; // ~330 feet
const APPROACH_THRESHOLD_METERS = 150; // ~500 feet - announce upcoming turn
const POSITION_UPDATE_INTERVAL_MS = 3000;

// ============================================================================
// VOICE NAVIGATION ENGINE CLASS
// ============================================================================

class VoiceNavigationEngineService {
  private isActive: boolean = false;
  private destination: NavigationDestination | null = null;
  private route: NavigationRoute | null = null;
  private currentStepIndex: number = 0;
  private announcedSteps: Set<number> = new Set();
  private lastAnnouncedApproach: number = -1;
  private watchId: number | null = null;
  private listeners: NavigationEventListener[] = [];
  private currentPosition: { lat: number; lng: number; heading?: number } | null = null;
  private lastPositionUpdate: number = 0;
  private isRerouting: boolean = false;

  // ==========================================================================
  // PUBLIC API
  // ==========================================================================

  /**
   * Start navigation to a well
   */
  async startNavigation(well: NavigationDestination): Promise<boolean> {
    if (this.isActive) {
      await this.stopNavigation();
    }

    try {
      this.destination = well;
      this.isActive = true;
      this.currentStepIndex = 0;
      this.announcedSteps.clear();
      this.lastAnnouncedApproach = -1;

      // Get current position
      const position = await this.getCurrentPosition();
      if (!position) {
        await this.speak('Unable to get your location. Please enable GPS and try again.');
        this.emit({ type: 'error', data: 'GPS unavailable' });
        this.isActive = false;
        return false;
      }

      this.currentPosition = position;

      // Get route from navigation service
      this.route = await navigationService.getDirections(
        position,
        { lat: well.latitude, lng: well.longitude }
      );

      if (!this.route || this.route.steps.length === 0) {
        // Fallback to compass navigation
        await this.speak(`Starting compass navigation to ${well.name}. Head toward the well location.`);
        this.startPositionTracking();
        this.emit({ type: 'started', data: { destination: well, mode: 'compass' } });
        return true;
      }

      // Announce navigation start
      const distanceMiles = (this.route.totalDistance * METERS_TO_MILES).toFixed(1);
      const etaMinutes = Math.round(this.route.totalDuration / 60);

      await this.speak(
        `Starting navigation to ${well.name}. ` +
        `${distanceMiles} miles away. ` +
        `Estimated arrival in ${etaMinutes} minute${etaMinutes !== 1 ? 's' : ''}.`
      );

      // Wait a moment then announce first instruction
      await this.delay(2000);
      await this.announceStep(0);

      // Start GPS tracking
      this.startPositionTracking();

      this.emit({ type: 'started', data: { destination: well, route: this.route } });
      toast.success(`Navigation started to ${well.name}`);

      return true;
    } catch (error: any) {
      console.error('Failed to start navigation:', error);
      await this.speak('Sorry, I could not start navigation. Please try again.');
      this.emit({ type: 'error', data: error.message });
      this.isActive = false;
      return false;
    }
  }

  /**
   * Stop navigation
   */
  async stopNavigation(): Promise<void> {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }

    const wasActive = this.isActive;
    this.isActive = false;
    this.destination = null;
    this.route = null;
    this.currentStepIndex = 0;
    this.announcedSteps.clear();
    this.currentPosition = null;

    if (wasActive) {
      await this.speak('Navigation stopped.');
      this.emit({ type: 'stopped' });
      toast.info('Navigation stopped');
    }
  }

  /**
   * Get current navigation progress
   */
  getProgress(): NavigationProgress {
    if (!this.isActive || !this.destination) {
      return {
        isActive: false,
        destination: null,
        currentStepIndex: 0,
        totalSteps: 0,
        distanceRemaining: 0,
        etaMinutes: 0,
        currentInstruction: '',
        isOffRoute: false,
        isArrived: false
      };
    }

    const currentStep = this.route?.steps[this.currentStepIndex];
    const nextStep = this.route?.steps[this.currentStepIndex + 1];

    // Calculate remaining distance
    let distanceRemaining = 0;
    if (this.route) {
      for (let i = this.currentStepIndex; i < this.route.steps.length; i++) {
        distanceRemaining += this.route.steps[i].distance;
      }
    }

    return {
      isActive: this.isActive,
      destination: this.destination,
      currentStepIndex: this.currentStepIndex,
      totalSteps: this.route?.steps.length || 0,
      distanceRemaining,
      etaMinutes: Math.round((this.route?.totalDuration || 0) / 60),
      currentInstruction: currentStep?.instruction || 'Continue to destination',
      nextInstruction: nextStep?.instruction,
      isOffRoute: false,
      isArrived: false
    };
  }

  /**
   * Check if navigation is active
   */
  isNavigating(): boolean {
    return this.isActive;
  }

  /**
   * Add event listener
   */
  addEventListener(listener: NavigationEventListener): void {
    this.listeners.push(listener);
  }

  /**
   * Remove event listener
   */
  removeEventListener(listener: NavigationEventListener): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  // Event emitter style aliases for convenience
  private eventHandlers: Map<string, Set<Function>> = new Map();

  /**
   * Subscribe to a specific event type (emitter-style API)
   */
  on(eventType: string, handler: Function): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, new Set());
    }
    this.eventHandlers.get(eventType)!.add(handler);
  }

  /**
   * Unsubscribe from a specific event type (emitter-style API)
   */
  off(eventType: string, handler: Function): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Emit to on/off style handlers (called from main emit)
   */
  private emitToHandlers(eventType: string, data?: any): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      const event = new CustomEvent(eventType, { detail: data });
      handlers.forEach(handler => {
        try {
          handler(event);
        } catch (error) {
          console.error(`Error in event handler for ${eventType}:`, error);
        }
      });
    }
  }

  /**
   * Manually trigger a step announcement
   */
  async repeatCurrentInstruction(): Promise<void> {
    if (!this.isActive || !this.route) {
      await this.speak('No active navigation.');
      return;
    }

    const step = this.route.steps[this.currentStepIndex];
    if (step) {
      await this.speak(this.humanizeInstruction(step, false));
    }
  }

  // ==========================================================================
  // POSITION TRACKING
  // ==========================================================================

  /**
   * Start GPS position tracking
   */
  private startPositionTracking(): void {
    if (!navigator.geolocation) {
      console.error('Geolocation not supported');
      return;
    }

    this.watchId = navigator.geolocation.watchPosition(
      (position) => this.handlePositionUpdate(position),
      (error) => this.handlePositionError(error),
      {
        enableHighAccuracy: true,
        maximumAge: 5000,
        timeout: 10000
      }
    );
  }

  /**
   * Handle GPS position update
   */
  private async handlePositionUpdate(position: GeolocationPosition): Promise<void> {
    if (!this.isActive || !this.destination) return;

    // Throttle updates
    const now = Date.now();
    if (now - this.lastPositionUpdate < POSITION_UPDATE_INTERVAL_MS) return;
    this.lastPositionUpdate = now;

    this.currentPosition = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      heading: position.coords.heading || undefined
    };

    // Check for arrival
    const distanceToDestination = this.calculateDistance(
      this.currentPosition.lat,
      this.currentPosition.lng,
      this.destination.latitude,
      this.destination.longitude
    );

    if (distanceToDestination <= ARRIVAL_THRESHOLD_METERS) {
      await this.handleArrival();
      return;
    }

    // If we have a route, check step progress
    if (this.route) {
      await this.checkStepProgress();
    } else {
      // Compass mode - just announce distance periodically
      await this.announceCompassProgress(distanceToDestination);
    }
  }

  /**
   * Handle GPS error
   */
  private handlePositionError(error: GeolocationPositionError): void {
    console.error('GPS error:', error);

    if (error.code === error.PERMISSION_DENIED) {
      this.speak('GPS permission denied. Please enable location services.');
      this.emit({ type: 'error', data: 'GPS permission denied' });
    } else if (error.code === error.POSITION_UNAVAILABLE) {
      // Silent - may just be temporary
    } else if (error.code === error.TIMEOUT) {
      // Silent - will retry automatically
    }
  }

  /**
   * Check progress along route steps
   */
  private async checkStepProgress(): Promise<void> {
    if (!this.route || !this.currentPosition) return;

    const navState = navigationService.calculateNavigationState(
      this.currentPosition,
      this.route,
      { lat: this.destination!.latitude, lng: this.destination!.longitude }
    );

    // Check for off-route
    if (navState.isOffRoute && !this.isRerouting) {
      await this.handleOffRoute();
      return;
    }

    // Check if we've moved to a new step
    if (navState.currentStepIndex > this.currentStepIndex) {
      this.currentStepIndex = navState.currentStepIndex;
      await this.announceStep(this.currentStepIndex);
      this.emit({ type: 'step_changed', data: { stepIndex: this.currentStepIndex } });
    }

    // Check if approaching next turn
    if (navState.distanceToNextStep <= APPROACH_THRESHOLD_METERS &&
      this.lastAnnouncedApproach !== this.currentStepIndex) {
      this.lastAnnouncedApproach = this.currentStepIndex;
      await this.announceApproachingTurn(navState.distanceToNextStep);
    }
  }

  // ==========================================================================
  // VOICE ANNOUNCEMENTS
  // ==========================================================================

  /**
   * Announce a navigation step
   */
  private async announceStep(stepIndex: number): Promise<void> {
    if (!this.route || this.announcedSteps.has(stepIndex)) return;

    const step = this.route.steps[stepIndex];
    if (!step) return;

    this.announcedSteps.add(stepIndex);

    const instruction = this.humanizeInstruction(step, true);
    await this.speak(instruction);
  }

  /**
   * Announce approaching turn
   */
  private async announceApproachingTurn(distanceMeters: number): Promise<void> {
    if (!this.route) return;

    const nextStep = this.route.steps[this.currentStepIndex];
    if (!nextStep || !nextStep.maneuver) return;

    const distanceFeet = Math.round(distanceMeters * METERS_TO_FEET);
    const maneuver = this.humanizeManeuver(nextStep.maneuver);

    if (distanceFeet <= 200) {
      await this.speak(`${maneuver} ahead.`);
    } else {
      await this.speak(`In ${distanceFeet} feet, ${maneuver.toLowerCase()}.`);
    }

    this.emit({ type: 'approaching_turn', data: { distance: distanceFeet, maneuver } });
  }

  /**
   * Announce compass navigation progress
   */
  private async announceCompassProgress(distanceMeters: number): Promise<void> {
    const distanceMiles = (distanceMeters * METERS_TO_MILES).toFixed(1);

    // Only announce at certain distance thresholds
    const thresholds = [5, 2, 1, 0.5, 0.25]; // miles
    const currentMiles = parseFloat(distanceMiles);

    for (const threshold of thresholds) {
      if (Math.abs(currentMiles - threshold) < 0.05) {
        if (threshold >= 1) {
          await this.speak(`${threshold} mile${threshold > 1 ? 's' : ''} to destination.`);
        } else {
          const feet = Math.round(threshold * 5280);
          await this.speak(`${feet} feet to destination.`);
        }
        break;
      }
    }
  }

  /**
   * Handle arrival at destination
   */
  private async handleArrival(): Promise<void> {
    await this.speak(`You have arrived at ${this.destination?.name || 'your destination'}.`);

    this.emit({ type: 'arrived', data: { destination: this.destination } });
    toast.success(`Arrived at ${this.destination?.name}`);

    // Auto-stop after arrival
    await this.delay(2000);
    await this.stopNavigation();
  }

  /**
   * Handle off-route detection
   */
  private async handleOffRoute(): Promise<void> {
    if (this.isRerouting) return;

    this.isRerouting = true;
    this.emit({ type: 'off_route' });

    await this.speak('You appear to be off route. Recalculating.');
    toast.info('Recalculating route...');

    try {
      // Get new route from current position
      if (this.currentPosition && this.destination) {
        this.emit({ type: 'rerouting' });

        const newRoute = await navigationService.getDirections(
          this.currentPosition,
          { lat: this.destination.latitude, lng: this.destination.longitude }
        );

        if (newRoute) {
          this.route = newRoute;
          this.currentStepIndex = 0;
          this.announcedSteps.clear();
          this.lastAnnouncedApproach = -1;

          await this.delay(1000);
          await this.announceStep(0);
        }
      }
    } catch (error) {
      console.error('Reroute failed:', error);
      await this.speak('Unable to recalculate route. Continuing to destination.');
    } finally {
      this.isRerouting = false;
    }
  }

  // ==========================================================================
  // HELPERS
  // ==========================================================================

  /**
   * Convert navigation instruction to natural speech
   */
  private humanizeInstruction(step: NavigationStep, includeDistance: boolean): string {
    let text = step.instruction
      .replace(/<[^>]+>/g, '') // Remove HTML tags
      .replace(/\bUS-(\d+)\b/g, 'US $1')
      .replace(/\bCR-?(\d+)\b/gi, 'County Road $1')
      .replace(/\bFM-?(\d+)\b/gi, 'Farm Road $1')
      .replace(/\bSH-?(\d+)\b/gi, 'State Highway $1')
      .replace(/\bI-?(\d+)\b/gi, 'Interstate $1')
      .replace(/\bHwy\b/gi, 'Highway')
      .replace(/\bRd\b/gi, 'Road')
      .replace(/\bSt\b/gi, 'Street')
      .replace(/\bAve\b/gi, 'Avenue')
      .replace(/\bBlvd\b/gi, 'Boulevard')
      .replace(/\bDr\b/gi, 'Drive')
      .replace(/\bLn\b/gi, 'Lane');

    if (includeDistance && step.distance > 100) {
      const miles = step.distance * METERS_TO_MILES;
      if (miles >= 0.1) {
        text += ` for ${miles.toFixed(1)} miles`;
      } else {
        const feet = Math.round(step.distance * METERS_TO_FEET / 100) * 100;
        text += ` for ${feet} feet`;
      }
    }

    return text;
  }

  /**
   * Convert maneuver to speech-friendly text
   */
  private humanizeManeuver(maneuver: string): string {
    const maneuvers: Record<string, string> = {
      'turn-right': 'Turn right',
      'turn-left': 'Turn left',
      'turn-slight-right': 'Bear right',
      'turn-slight-left': 'Bear left',
      'turn-sharp-right': 'Sharp right',
      'turn-sharp-left': 'Sharp left',
      'uturn-right': 'Make a U-turn',
      'uturn-left': 'Make a U-turn',
      'straight': 'Continue straight',
      'merge': 'Merge',
      'ramp-left': 'Take the ramp on the left',
      'ramp-right': 'Take the ramp on the right',
      'fork-left': 'Keep left at the fork',
      'fork-right': 'Keep right at the fork',
      'roundabout-left': 'At the roundabout, turn left',
      'roundabout-right': 'At the roundabout, turn right'
    };
    return maneuvers[maneuver] || maneuver.replace(/-/g, ' ');
  }

  /**
   * Get current GPS position
   */
  private getCurrentPosition(): Promise<{ lat: number; lng: number } | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 15000 }
      );
    });
  }

  /**
   * Calculate distance between two points (Haversine formula)
   */
  private calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000; // Earth's radius in meters
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  /**
   * Speak text
   */
  private async speak(text: string): Promise<void> {
    return new Promise((resolve) => {
      textToSpeech.speak(text, { onEnd: () => resolve() });
    });
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Emit event to listeners
   */
  private emit(event: Omit<NavigationEvent, 'timestamp'>): void {
    const fullEvent: NavigationEvent = { ...event, timestamp: Date.now() };

    // Emit to addEventListener-style listeners
    this.listeners.forEach(listener => {
      try {
        listener(fullEvent);
      } catch (e) {
        console.error('Navigation event listener error:', e);
      }
    });

    // Also emit to on/off-style handlers (map event types)
    const eventTypeMap: Record<string, string> = {
      'step_changed': 'step-change',
      'off_route': 'off-route',
      'arrived': 'arrival',
      'rerouting': 'reroute'
    };
    const mappedType = eventTypeMap[event.type] || event.type;
    this.emitToHandlers(mappedType, event.data);
  }
}

// Export singleton instance
export const voiceNavigationEngine = new VoiceNavigationEngineService();

// Export class for testing
export { VoiceNavigationEngineService };
